// Copyright 2024, Imprivata, Inc.  All rights reserved.
import { useQuery } from '@tanstack/react-query';
import { readServerFlag, FEATURE_FLAGS_QUERY_KEY } from '@featureFlags/server-cache';
import { QueryKey } from '@portal/constants';
import { _getEnv } from '@api/utils';
import { FEATURE_FLAG_REGISTRY, type FeatureFlagKey, type FlagValue } from '@featureFlags/registry';
import featureFlags from '@featureFlags/flags.json';

export type FeatureFlags = typeof featureFlags;

export enum ENVIRONMENT {
  local = 1,
  dev = 2,
  int = 3,
  eph = 4,
  stg = 5,
  prod = 6,
  prod_eu = 7,
  prod_au = 8,
}

export const TldToEnv: Record<string, ENVIRONMENT> = {
  au: ENVIRONMENT.prod_au,
  eu: ENVIRONMENT.prod_eu,
};

export type FeatureEnvironment = keyof typeof ENVIRONMENT;

export type FeatureConfig = {
  PENDO_API_KEY: string;
  ENABLE_EPCS: boolean;
  ENABLE_LOGIN_ACTIVITY_REPORT: boolean;
  ENABLE_ENROLLMENT_ACTIVITY_REPORT: boolean;
  PASSWORD_EXPIRATION_ENABLED: boolean;
  PASSWORD_EXPIRATION_ENABLED_TENANTS?: string[];
  ENABLE_EMAIL_OTP?: boolean;
  ENABLE_TEST_ENTRA_ID?: boolean;
  ENABLE_ENDPOINTS?: boolean;
  ENABLE_CUSTOM_IDV_TEMPLATE?: boolean;
};

function legacyFeatureSupported(feature: keyof FeatureConfig): boolean {
  const env = _getEnv();
  let featureEnv: FeatureEnvironment = 'prod';

  if (ENVIRONMENT[env]) {
    featureEnv = ENVIRONMENT[env] as unknown as FeatureEnvironment;
  }
  const currentLevel = ENVIRONMENT[featureEnv].valueOf();
  const featureLevel = Object.keys(ENVIRONMENT)
    .filter(key => Number.isNaN(Number(key)))
    .find(key => {
      const envFeatures = featureFlags[key.toLowerCase() as keyof FeatureFlags];
      return (
        feature in envFeatures &&
        typeof envFeatures[feature as keyof typeof envFeatures] === 'boolean' &&
        envFeatures[feature as keyof typeof envFeatures]
      );
    });

  console.debug('[featureFlagUtils] feature flags: ', { feature, featureEnv, currentLevel, featureLevel });

  if (featureLevel) {
    return currentLevel <= ENVIRONMENT[featureLevel as keyof typeof ENVIRONMENT];
  }

  return false;
}

/**
 * @returns TRUE if the feature is enabled for the current session.
 * For legacy flags: evaluated against featureFlags.json env ladder.
 * For server flags: read from the BFF-primed cache (call from component body, not module scope).
 */
export function featureSupported(feature: FeatureFlagKey): boolean {
  const entry = FEATURE_FLAG_REGISTRY[feature];
  if (entry && !('legacy' in entry)) {
    if (entry.featureFlagType !== 'boolean') {
      console.error(`[featureFlagUtils] featureSupported called on non-boolean flag: ${feature}`);
      return false;
    }
    return readServerFlag(entry.featureFlagKey, 'boolean', entry.default) as boolean;
  }
  return legacyFeatureSupported(feature as keyof FeatureConfig);
}

export function getFeatureFlagValue(feature: FeatureFlagKey): FlagValue {
  const entry = FEATURE_FLAG_REGISTRY[feature];
  if (!('legacy' in entry)) {
    return readServerFlag(entry.featureFlagKey, entry.featureFlagType, entry.default);
  }
  const flags = getFeatureFlags();
  return flags[feature as keyof typeof flags] as FlagValue;
}

export function getFeatureFlags() {
  const currentEnv = _getEnv();
  const envName = ENVIRONMENT[currentEnv] as keyof FeatureFlags;
  let features = featureFlags['prod' as keyof FeatureFlags];

  if (envName && envName in featureFlags) {
    features = featureFlags[envName];
  }

  console.debug('[featureFlagUtils] feature flags: ', { currentEnv, envName, features });
  return features;
}

export function isPasswordExpirationEnabledForTenant(tenantId: string): boolean {
  const features = getFeatureFlags();
  if (features.PASSWORD_EXPIRATION_ENABLED) {
    return true;
  }
  const enabledTenants = features.PASSWORD_EXPIRATION_ENABLED_TENANTS || [];
  return enabledTenants.includes(tenantId);
}

export function useFeatureFlagValue(feature: FeatureFlagKey): FlagValue {
  const { data: session } = useQuery<{ tenantId?: string } | null>({
    queryKey: [QueryKey.CLIENT_USER_SESSION],
    enabled: false,
  });
  useQuery({
    queryKey: [FEATURE_FLAGS_QUERY_KEY, session?.tenantId],
    enabled: false,
  });
  return getFeatureFlagValue(feature);
}
